// frontend/src/components/Register/Register.tsx

import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import './Register.css';

// ============================================================
// VALIDAÇÃO DE E-MAIL — mesma regex do backend (auth.controller.ts)
// ============================================================
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const Register: React.FC = () => {
  const location = useLocation();
  const [name, setName] = useState('');
  const [email, setEmail] = useState((location.state as any)?.email || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [company, setCompany] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    // ============================================================
    // VALIDAÇÕES LOCAIS — mesmas regras do backend
    // ============================================================
    if (!name || !email || !password || !confirmPassword) {
      setError('Preencha todos os campos obrigatórios');
      setLoading(false);
      return;
    }

    if (name.trim().length < 3) {
      setError('Nome deve ter pelo menos 3 caracteres');
      setLoading(false);
      return;
    }

    if (!EMAIL_REGEX.test(email)) {
      setError('Email inválido. Use o formato nome@dominio.com');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Senha deve ter pelo menos 6 caracteres');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      setLoading(false);
      return;
    }

    try {
      await register({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        phone: phone.trim() || undefined,
        company: company.trim() || undefined,
      });

      // ⭐ Cadastro já autentica — redireciona para o dashboard
      setSuccess('✅ Cadastro realizado com sucesso! Redirecionando...');
      setTimeout(() => {
        navigate('/dashboard');
      }, 800);

    } catch (err: any) {
      // ============================================================
      // MENSAGENS AMIGÁVEIS POR STATUS
      // ============================================================
      const status = err.response?.status;
      const message = err.response?.data?.message;

      if (status === 400) {
        setError(message || 'Dados inválidos. Verifique os campos.');
      } else if (status === 409) {
        setError('Este email já está cadastrado. Faça login ou use outro email.');
      } else if (status === 500) {
        setError('Erro no servidor. Tente novamente em instantes.');
      } else if (!err.response) {
        setError('Não foi possível contatar o servidor. Verifique sua conexão.');
      } else {
        setError(message || err.message || 'Erro ao cadastrar.');
      }
      setLoading(false);
    }
  };

  return (
    <div className="register-container">
      <div className="register-card">
        <div className="register-header">
          <h1>📝 Criar Conta</h1>
          <p>Preencha os dados para se cadastrar</p>
        </div>

        <form onSubmit={handleSubmit} className="register-form">
          <div className="form-group">
            <label htmlFor="name">👤 Nome Completo *</label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Seu nome completo"
              disabled={loading}
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="email">📧 Email *</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="seu@email.com"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="phone">📱 Telefone</label>
            <input
              id="phone"
              type="text"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(11) 99999-9999"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="company">🏢 Empresa</label>
            <input
              id="company"
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Nome da empresa"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">🔑 Senha *</label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Mínimo 6 caracteres"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">✅ Confirmar Senha *</label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Digite a senha novamente"
              disabled={loading}
            />
          </div>

          {error && (
            <div className="error-message">
              ❌ {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              {success}
            </div>
          )}

          <button
            type="submit"
            className="register-button"
            disabled={loading}
          >
            {loading ? '⏳ Cadastrando...' : '📝 Cadastrar'}
          </button>

          <div className="register-footer">
            <span>Já tem uma conta?</span>
            <Link to="/login" className="login-link">
              Faça login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};